// Client for the ACCOUNT-CLAIM endpoints (the magic link both patients and providers
// land on). Kept separate from api.ts (patient funnel) and providerApi.ts (provider
// application) so the three pre-auth flows never entangle.
//
// Server contract — expy-health-api/server/routes/patientInvite/index.js. Despite the
// route name it is deliberately KIND-AGNOSTIC: it only sets the permanent Cognito
// password, so the same endpoints serve provider claims too.
//
//   GET  /patient-invite/validate?token=…            -> { valid: true, firstName?, expiresAt? }
//                                                    -> { valid: false, reason }   (still HTTP 200)
//   POST /patient-invite/claim {token, password, smsConsent?} -> { email }
//        410 → invite already used or expired ; 400 → bad token or rejected password
//
// Error bodies are `{ error: "<message>" }`. On 400 that message carries the Cognito
// password-policy failure, so it is parsed out and surfaced rather than swallowed.
import { ApiError, RateLimited } from './api';

const BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') || '';

export type InviteInvalidReason = 'missing' | 'invalid' | 'used' | 'expired';

export interface ValidateResult {
  valid: boolean;
  firstName?: string;
  expiresAt?: string;
  reason?: InviteInvalidReason;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    });
  } catch (e) {
    throw new ApiError(`network: ${(e as Error).message}`, 0);
  }
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After'));
    throw new RateLimited(Number.isFinite(retryAfter) ? retryAfter * 1000 : 3000);
  }
  if (!res.ok) {
    let message = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: unknown };
      if (typeof body?.error === 'string' && body.error.trim()) message = body.error;
    } catch {
      /* non-JSON body — keep the generic marker */
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

export function validateInvite(token: string): Promise<ValidateResult> {
  return request<ValidateResult>(`/patient-invite/validate?token=${encodeURIComponent(token)}`);
}

export function claimInvite(args: {
  token: string;
  password: string;
  smsConsent?: boolean;
}): Promise<{ email: string }> {
  return request<{ email: string }>(`/patient-invite/claim`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}
